import React from 'react';
import { useQuery } from '@apollo/client';
import { copyFor, routes } from 'config';
import { useAccountContext } from 'contexts/account';
import { ErrorModule } from 'components/errors';
import { StandardRow, StandardRowLoading } from 'components/standard-row';
import { useAccountPlansCount } from 'hooks/use-account-plans-count.hook';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GET_ACCOUNT_SETTINGS_INFO } from './account-settings.api';
import { AccountSettingsBase } from './account-settings.base';
import {
  getNumberOfUtilities,
  getUtilitySyncRoute,
  getNumberOfPaymentMethods,
} from './account-settings.utils';

const getCopy = copyFor('settings.accountSettings');
const { payment, settings, accountDetails, plans, checkLmiEligibility } =
  routes();

export const AccountSettingsActive = () => {
  const flag = useFlags();
  const plansCount = useAccountPlansCount();
  const { selectedAccountId } = useAccountContext();
  const {
    data,
    error,
    loading: infoLoading,
  } = useQuery<
    GetAccountSettingsInfoQuery,
    GetAccountSettingsInfoQueryVariables
  >(GET_ACCOUNT_SETTINGS_INFO, {
    variables: { accountId: selectedAccountId },
  });

  const { account } = data || {};
  const utilityAccounts = account?.utilityAccounts || [];
  const isBusinessUser =
    account?.customerType === 'smb' ||
    account?.user?.originationSource?.funnel === 'business';

  if (error || plansCount.error)
    return <ErrorModule title={getCopy('title')} margin={{ bottom: '24px' }} />;
  const loading = infoLoading || plansCount.loading;

  const numberOfActivePlans = plansCount.data?.active;
  const utilityCredentials = account?.utilityCredentials || [];
  const paymentMethods = account?.paymentMethods || [];
  const utilitySyncRoute = getUtilitySyncRoute(utilityCredentials);
  const numberOfUtilities = getNumberOfUtilities(utilityCredentials);
  const numberOfPaymentMethods = getNumberOfPaymentMethods(paymentMethods);
  const showUtilitySync = numberOfUtilities > 0;
  const isIncorrectVerification = utilityCredentials.some(
    credential => credential.verificationStatus === 'incorrect'
  );
  const warningMessage = isIncorrectVerification
    ? getCopy('utilitySync.warningText')
    : null;
  // Feature flag: cs-acb-retirement
  // When enabled, use targetBillExperience to determine if payment methods should be shown
  const csAcbRetirement = flag.csAcbRetirement;
  const UTILITY_BILLED = 'utility_billed';
  const utilityBilled = utilityAccounts.every(
    ({ targetBillExperience, activeBillingSettings, funnelBillExperience }) => {
      const billExperience = csAcbRetirement
        ? (targetBillExperience as string) ??
          activeBillingSettings?.billExperience ??
          funnelBillExperience
        : activeBillingSettings?.billExperience ?? funnelBillExperience;
      return billExperience === UTILITY_BILLED;
    }
  );

  return (
    <AccountSettingsBase>
      {loading ? (
        <LoadingRows />
      ) : (
        <>
          <StandardRow
            iconLinkProps={{ to: accountDetails }}
            includesBuffer={true}
            heading={getCopy('accountDetails.heading')}
            headingTag="h3"
            text={getCopy('accountDetails.subheading', {
              accountName: account?.name,
            })}
            bufferWidth="32px"
            mobileBufferWidth="32px"
          />
          <StandardRow
            iconLinkProps={{ to: plans }}
            includesBuffer={true}
            heading={getCopy('planDetails.heading')}
            headingTag="h3"
            text={getCopy('planDetails.subheading', { numberOfActivePlans })}
            bufferWidth="32px"
            mobileBufferWidth="32px"
          />
          {(!utilityBilled ||
            numberOfPaymentMethods >= 1 ||
            isBusinessUser) && (
            <StandardRow
              iconLinkProps={{ to: payment }}
              includesBuffer={true}
              heading={getCopy('paymentMethods.heading')}
              headingTag="h3"
              text={
                numberOfPaymentMethods === 1
                  ? getCopy('paymentMethods.numberOfMethods.one')
                  : getCopy('paymentMethods.numberOfMethods.notOne', {
                      number: numberOfPaymentMethods,
                    })
              }
              bufferWidth="32px"
              mobileBufferWidth="32px"
            />
          )}
          {showUtilitySync && (
            <StandardRow
              iconLinkProps={{
                to: { pathname: utilitySyncRoute, state: { from: settings } },
              }}
              includesBuffer={true}
              heading={getCopy('utilitySync.heading')}
              headingTag="h3"
              text={
                numberOfUtilities === 1
                  ? getCopy('utilitySync.numberOfUtilities.one')
                  : getCopy('utilitySync.numberOfUtilities.notOne', {
                      number: numberOfUtilities,
                    })
              }
              warning={warningMessage}
              bufferWidth="32px"
              mobileBufferWidth="32px"
            />
          )}
          {flag.lbCheckLmiEligibility && !isBusinessUser && (
            <StandardRow
              iconLinkProps={{ to: checkLmiEligibility }}
              includesBuffer={true}
              heading={getCopy('checkLmiEligibility.heading')}
              headingTag="h3"
              text={getCopy('checkLmiEligibility.copy')}
              bufferWidth="32px"
              mobileBufferWidth="32px"
            />
          )}
        </>
      )}
    </AccountSettingsBase>
  );
};

export const LoadingRows = () => (
  <>
    <StandardRowLoading
      includesBuffer={true}
      bufferWidth="32px"
      mobileBufferWidth="32px"
    />
    <StandardRowLoading
      includesBuffer={true}
      bufferWidth="32px"
      mobileBufferWidth="32px"
    />
    <StandardRowLoading
      includesBuffer={true}
      bufferWidth="32px"
      mobileBufferWidth="32px"
    />
    <StandardRowLoading
      includesBuffer={true}
      bufferWidth="32px"
      mobileBufferWidth="32px"
    />
    <StandardRowLoading
      includesBuffer={true}
      bufferWidth="32px"
      mobileBufferWidth="32px"
    />
  </>
);
